import { Button, TableCell, TableRow, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { IAuditedMaterial, IComboBoxOption, IMaterialAuditResult } from "../../interfaces";
import { MaterialDetailsDialog } from "../MaterialDetailsDialog";
import { MaterialIdentifiers } from "../Shared/MaterialIdentifiers";
import { MaterialLocation } from "../Shared/MaterialLocation";
import AuditComparison from "./AuditComparison";

export const MaterialAuditTableRow: React.FC<{
    key?: string;
    materialAuditResult: IMaterialAuditResult;
    materialStates?: IComboBoxOption[],
    onRemove: () => void;
    onUpdate: (materialAuditResult: IMaterialAuditResult) => void;
    onReloadMaterial?: () => void;
    readonly: boolean;
}> = ({ materialAuditResult, materialStates, onRemove, onUpdate, onReloadMaterial, readonly, key }) => {
    // Local state for the materialAuditResult and material
    const [localAuditResult, setLocalAuditResult] = useState<IMaterialAuditResult>(materialAuditResult);
    const [material, setMaterial] = useState<IAuditedMaterial>(
        (readonly && materialAuditResult.materialStatic) ?
            materialAuditResult.materialStatic as IAuditedMaterial :
            materialAuditResult.material
    );

    useEffect(() => {
        setLocalAuditResult(materialAuditResult);
        setMaterial(
            (readonly && materialAuditResult.materialStatic) ?
                materialAuditResult.materialStatic as IAuditedMaterial :
                materialAuditResult.material
        );
    }, [materialAuditResult, readonly]);

    const handleUpdate = (value: string | number, property: keyof IMaterialAuditResult & keyof IAuditedMaterial) => {
        const updatedAuditResult = { ...localAuditResult, [property]: value };
        const updatedMaterial = { ...material };

        if (property in updatedAuditResult) {
            (updatedAuditResult as any)[property] = value;
        }
        if (property in updatedMaterial) {
            (updatedMaterial as any)[property] = value;
        }

        setLocalAuditResult(updatedAuditResult);
        setMaterial(updatedMaterial);

        onUpdate({ ...updatedAuditResult, material: updatedMaterial });
    };

    return (
        <TableRow className="material-row" key={key}>
            <TableCell>
                <p>{material.classes}</p>
            </TableCell>
            <TableCell>
                <MaterialLocation material={material} />
            </TableCell>
            <TableCell>
                <MaterialIdentifiers material={material} />
            </TableCell>
            <TableCell>
                <div>
                    <p><strong>{material.l3}</strong></p>
                    <small>{material.l1?.value}</small>
                    <small>{material.l2?.value}</small>
                </div>
            </TableCell>

            <AuditComparison
                originalValue={material.quantity?.toString() ?? ""}
                auditValue={localAuditResult.qtyAudit?.toString()}
                onUpdate={(value) => handleUpdate(value, "qtyAudit")}
                readonly={readonly}
            />

            <AuditComparison
                originalValue={material.state?.value ?? ""}
                auditValue={localAuditResult.stateAudit}
                options={materialStates?.map(x => x.value) as string[] ?? []}
                onUpdate={(value) => handleUpdate(value, "stateAudit")}
                readonly={readonly}
            />

            <AuditComparison
                originalValue={material.storageReason ?? ""}
                auditValue={localAuditResult.storageReasonAudit}
                as={"textarea"}
                onUpdate={(value) => handleUpdate(value, "storageReasonAudit")}
                readonly={readonly}
            />

            <AuditComparison
                auditValue={localAuditResult.retentionDecision}
                options={["Keep", "Dispose", "TBC"]}
                onUpdate={(value) => handleUpdate(value, "retentionDecision")}
                readonly={readonly}
            />

            <AuditComparison
                auditValue={localAuditResult.auditComments}
                as={"textarea"}
                onUpdate={(value) => handleUpdate(value, "auditComments")}
                readonly={readonly}
            />

            <TableCell width="45px" style={{ textAlign: "center", verticalAlign: "middle" }}>
                <MaterialDetailsDialog
                    material={material}
                    viewMode={readonly ? "material_audit_static" : "material_audit"}
                    onReloadMaterial={onReloadMaterial}
                />
                <Tooltip title="Remove from audit">
                    <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={onRemove}
                        disabled={readonly}
                        sx={{ mt: 1 }}
                    >
                        Remove
                    </Button>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
};
